import React, { useState, useRef, useEffect } from 'react';
import { Send, SkipForward, X, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

export const TextChat = ({ 
  status, 
  messages, 
  onSendMessage, 
  onNext, 
  onDisconnect, 
  onBack 
}) => {
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (status === 'matched' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [status]);

  const handleSend = (e) => {
    e.preventDefault();
    if (message.trim() && status === 'matched') {
      onSendMessage(message.trim());
      setMessage('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'waiting':
        return 'Looking for a stranger...';
      case 'matched':
        return 'Connected to a stranger';
      case 'partner_disconnected':
        return 'Stranger disconnected';
      default:
        return 'Connecting...';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'waiting':
        return 'text-yellow-600';
      case 'matched':
        return 'text-green-600';
      case 'partner_disconnected':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onBack}
            className="hover:bg-white/20"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Text Chat</h1>
            <p className={`text-sm ${getStatusColor()}`}>
              {getStatusText()}
            </p>
          </div>
        </div>
        
        <div className="flex gap-2">
          {status === 'matched' && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onNext}
              className="bg-white/80 hover:bg-white"
            >
              <SkipForward className="w-4 h-4 mr-2" />
              Next
            </Button>
          )}
          <Button 
            variant="destructive" 
            size="sm" 
            onClick={onDisconnect}
          >
            <X className="w-4 h-4 mr-2" />
            Stop
          </Button>
        </div>
      </div>

      {/* Chat Area */}
      <Card className="flex-1 flex flex-col bg-white/80 backdrop-blur-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">
            {status === 'waiting' ? (
              <div className="flex items-center gap-2">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-blue-500 rounded-full pulse-dot"></div>
                  <div className="w-2 h-2 bg-blue-500 rounded-full pulse-dot"></div>
                  <div className="w-2 h-2 bg-blue-500 rounded-full pulse-dot"></div>
                </div>
                <span>Finding someone for you...</span>
              </div>
            ) : status === 'matched' ? (
              'You are now chatting with a stranger'
            ) : status === 'partner_disconnected' ? (
              'Stranger has disconnected'
            ) : (
              'Connecting...'
            )}
          </CardTitle>
        </CardHeader>
        
        <CardContent className="flex-1 flex flex-col overflow-hidden">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto mb-4 space-y-3 min-h-0">
            {messages.length === 0 && status === 'matched' && (
              <div className="text-center text-gray-500 py-8">
                <p>Say hello to start the conversation!</p>
              </div>
            )}
            
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${
                  msg.sender === 'me' ? 'justify-end' : 'justify-start'
                }`}
              >
                <div
                  className={`max-w-xs md:max-w-md lg:max-w-lg px-4 py-2 rounded-lg ${
                    msg.sender === 'me'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-900'
                  }`}
                >
                  <p className="text-sm">{msg.text}</p>
                  <p className="text-xs opacity-70 mt-1">
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            ))}
            
            {status === 'partner_disconnected' && (
              <div className="text-center text-gray-500 py-4">
                <p>The stranger has left the chat</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={onNext}
                  className="mt-2"
                >
                  Find another stranger
                </Button>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input */}
          <form onSubmit={handleSend} className="flex gap-2">
            <Input
              ref={inputRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={
                status === 'matched' 
                  ? 'Type a message...' 
                  : 'Waiting for connection...'
              }
              disabled={status !== 'matched'}
              className="flex-1"
            />
            <Button 
              type="submit" 
              size="icon" 
              disabled={!message.trim() || status !== 'matched'}
              className="bg-blue-500 hover:bg-blue-600"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}; 